import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { MODES, type ModeId } from "../domain/enums";
import { useLanguage } from "../state/language";
import { modeName, modeDesc } from "../domain/i18n";
import { MODE_EMOJI, MODE_THEMES, modeStyle } from "../components/modeTheme";
import { Modal } from "../components/Modal";
import { Button } from "../components/Button";
import { getActiveSession, getPackById } from "../db";
import type { Session, Pack } from "../domain/types";

export default function Modes() {
  const nav = useNavigate();
  const { lang, t } = useLanguage();
  const language = lang ?? "uk";
  const [pendingAdult, setPendingAdult] = useState<ModeId | null>(null);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [activePack, setActivePack] = useState<Pack | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = (await getActiveSession()) ?? null;
      if (cancelled) return;
      setActiveSession(s);
      if (s) {
        const p = (await getPackById(s.packId)) ?? null;
        if (!cancelled) setActivePack(p);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function onPickMode(modeId: ModeId, isAdult: boolean) {
    if (isAdult) setPendingAdult(modeId);
    else nav(`/packs/${modeId}`);
  }

  return (
    <div className="pp-screen px-5">
      <div className="pp-aurora" aria-hidden />
      <header className="pt-4">
        {/* Brand row — mini portal mark + wordmark eyebrow. */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span
              className="pp-portal shrink-0"
              style={{ width: 18, height: 18, ["--ring" as never]: "5px" }}
              aria-hidden
            />
            <span className="text-[11px] uppercase tracking-[0.28em] opacity-60 font-bold">
              {t("app_title")}
            </span>
          </div>
          <button
            aria-label={t("settings_title")}
            className="rounded-full p-3 bg-white/10 active:scale-95"
            onClick={() => nav("/settings")}
          >
            ⚙
          </button>
        </div>
        <h2 className="font-display text-4xl font-black tracking-tight mt-3">
          {t("modes_title")}
        </h2>
      </header>
      {activeSession && activePack && (
        <button
          onClick={() => nav(`/session/${activeSession.id}`)}
          className="mt-4 w-full rounded-2xl px-4 py-3 text-left flex items-center gap-3 border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 active:scale-[0.99] transition animate-glow"
          style={{ ["--flare" as never]: "#3df0a3" }}
        >
          <span className="text-2xl">▶</span>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider opacity-70">
              {t("resume_session")}
            </div>
            <div className="font-display text-lg font-bold truncate">
              {activePack.titleByLang[language] || activePack.titleByLang.uk}
            </div>
          </div>
          <div className="text-xs tabular-nums opacity-80 shrink-0">
            {activeSession.cardsSeen}/{activeSession.totalCards}
          </div>
        </button>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4">
        {MODES.map((m) => {
          const accent = MODE_THEMES[m.id].accent;
          return (
            <button
              key={m.id}
              onClick={() => onPickMode(m.id, m.isAdult)}
              className="relative overflow-hidden rounded-3xl p-5 text-left active:scale-[0.985] transition border border-white/10"
              style={modeStyle(m.id)}
            >
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  background: `radial-gradient(closest-side,${MODE_THEMES[m.id].flare}55,transparent 70%)`,
                }}
              />
              <div className="relative flex items-center gap-4">
                {/* Glyph tile — the mode's identity mark, tinted by its accent. */}
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shrink-0"
                  style={{
                    background: `${accent}26`,
                    border: `1px solid ${accent}59`,
                    boxShadow: `inset 0 0 18px ${accent}33`,
                  }}
                  aria-hidden
                >
                  {MODE_EMOJI[m.id]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-2xl font-extrabold truncate">
                      {modeName(language, m.id)}
                    </h3>
                    {m.isAdult && (
                      <span className="rounded-full bg-black/40 text-[11px] px-2 py-0.5 border border-white/20 shrink-0">
                        18+
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] opacity-75 mt-0.5 leading-snug">
                    {modeDesc(language, m.id)}
                  </p>
                </div>
                <span className="opacity-40 text-2xl shrink-0" aria-hidden>
                  ›
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <Modal open={pendingAdult != null} title={t("age_gate_title")}>
        <p className="opacity-80">{t("age_gate_body")}</p>
        <div className="mt-5 flex gap-3">
          <Button variant="ghost" fullWidth onClick={() => setPendingAdult(null)}>
            {t("cta_cancel")}
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={() => {
              const id = pendingAdult;
              setPendingAdult(null);
              if (id) nav(`/packs/${id}`);
            }}
          >
            {t("age_gate_confirm")}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
